import React, { useState, useEffect } from 'react';
import { Route, Switch, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Row } from 'reactstrap';

import axios from './axios-base';
import Home from './containers/Home/Home';
import SignUp from './containers/SignUp/SignUp';
import ProjectList from './containers/ProjectList/ProjectList';
import Project from './containers/Project/Project';
import Page from './components/Page/Page';
import Auth from './containers/Auth/Auth';
import Logout from './containers/Auth/Logout/Logout';
import JuryShell from './containers/JuryVoting/JuryShell/JuryShell';
import withErrorHandler from './hoc/withErrorHandler/withErrorHandler';
import * as actions from './store/actions/index';
import Header from './components/Header/Header';
import Footer from './components/Footer/Footer';
import ConsentModal from './components/UI/Modal/ConsentModal/ConsentModal';
import Cookies from 'js-cookie';
import { Helmet } from 'react-helmet';
import { personalization, statisticTracking } from './shared/trackingScripts';
import ScrollToTop from './shared/scrollToTop';
import PageNotFound from './components/Page/PageNotFound.js';
import LivePreviewPage from './components/Page/LivePreviewPage';
import SMSRequest from './containers/SMSRequest/SMSRequest';
import { useFooterConfig, useMetricsConfig } from './store/designStore';
import { useIsPreview } from './shared/previewHelper';
import useDesignLivePreview from './store/useDesignLivePreview';
import { useRegisterFocusMode, useComputedFocusMode, FocusModePriority } from './store/focusModeStore';

function App(props) {
    const location = useLocation();
    const dispatch = useDispatch();
    useDesignLivePreview();
    const isJuryRoot = location.pathname.startsWith('/jury');
    const {
        gap: { desktop: metricsGapValueDesktop, mobile: metricsGapValueMobile } = {},
    } = useMetricsConfig();
    const containerClasses = [
        'container-lg',
        'main-content-container',
        metricsGapValueDesktop !== undefined ? 'desktop-gap-configured' : '',
        metricsGapValueMobile !== undefined ? 'mobile-gap-configured' : '',
    ]
        .filter(Boolean)
        .join(' ');
    const [pageNotFound, setPageNotFound] = useState(false);
    const [pages, setPages] = useState(null);
    const [navigation, setNavigation] = useState({});
    const [previewPageData, setPreviewPageData] = useState(null);

    const pageNotFoundCallback = (pageState) => {
        setPageNotFound(pageState);
    };

    useEffect(() => {
        dispatch(actions.authCheckState());
        const pages = axios.get('/pages/');
        const navigation = axios.get('/menus/');

        Promise.all([pages, navigation])
            .then(([resPages, resNavigation]) => {
                if (process.env.REACT_APP_BUILD_FOR_ARCHIVAL === 'true') {
                    resNavigation.data = resNavigation.data.filter((item) => item.linkTarget !== '/bewerbung');
                }

                const primaryNavigation = resNavigation.data.filter((menuItem) => {
                    return menuItem.position === 'primary' && menuItem.highlight === false;
                });
                const primaryNavigationCTA = resNavigation.data.filter((menuItem) => {
                    return menuItem.position === 'primary' && menuItem.highlight === true;
                });
                const secondaryNavigation = resNavigation.data.filter((menuItem) => {
                    return menuItem.position === 'secondary';
                });
                setPages(resPages.data);
                setNavigation({
                    primaryNavigation: primaryNavigation,
                    primaryNavigationCTA: primaryNavigationCTA,
                    secondaryNavigation: secondaryNavigation,
                });
            })
            .catch((error) => {
                console.log(error);
            });
    }, [dispatch]);

    let dynamicPages = null;
    let dynamicHeader = null;
    let consentManagementModal = null;
    let pageNotFoundRoute = null;
    const isPreview = useIsPreview();

    // Check if cookies exist and display consent management modal OR load tracking scripts (suppressed in preview mode)
    if (!isPreview) {
        if (
            Cookies.get('bigfoot_personalization-consent') === 'true' &&
            Cookies.get('bigfoot_statistic-consent') === 'true'
        ) {
            dynamicHeader = (
                <Helmet>
                    {personalization()}
                    {statisticTracking()}
                </Helmet>
            );
        } else if (Cookies.get('bigfoot_statistic-consent') === 'true') {
            dynamicHeader = <Helmet>{statisticTracking()}</Helmet>;
        } else if (Cookies.get('bigfoot_personalization-consent') === 'true') {
            dynamicHeader = <Helmet>{personalization()}</Helmet>;
        }

        if (Cookies.get('bigfoot_cookie-consent') !== 'true') {
            consentManagementModal = <ConsentModal pages={pages} />;
        }
    }

    if (pages) {
        pageNotFoundRoute = (
            <Route path="*" status={404} render={() => <PageNotFound pageNotFoundCallback={pageNotFoundCallback} />} />
        );
        dynamicPages = pages
            .filter((page) => !page.slug.startsWith('jury/'))
            .map((page, i) => {
                return (
                    /* The following component is monitored by Font Awesome dom.watch(). Check children */
                    <Route
                        key={i}
                        exact //Caution: This may redirect to 404 pag if embedded links are opened from the page.
                        path={`/${page.slug}`}
                        render={(props) => (
                            <Row>
                                <Page
                                    pageTitle={page.title}
                                    showTitle={page.showTitle}
                                    pageBody={page.body}
                                    survey={page.survey}
                                    hasExclusiveBlock={page.hasExclusiveBlock}
                                    {...props}
                                />
                            </Row>
                        )}
                    />
                );
            });
    }

    const { isFullWidth: isFooterFullWidth } = useFooterConfig();

    const currentSlug = location.pathname.startsWith('/') ? location.pathname.substring(1) : location.pathname;
    const currentPage = pages?.find((page) => page.slug === currentSlug);

    const globalFocusDesktop = isPreview
        ? Boolean(previewPageData?.isFocusModeDesktop)
        : Boolean(currentPage?.isFocusModeDesktop);
    const globalFocusMobile = isPreview
        ? Boolean(previewPageData?.isFocusModeMobile)
        : Boolean(currentPage?.isFocusModeMobile);

    useRegisterFocusMode('global', FocusModePriority.PAGE, globalFocusDesktop, globalFocusMobile);

    const { isFocusModeDesktop, isFocusModeMobile } = useComputedFocusMode();

    return (
        <div className="w-100 overflow-x-hidden">
            {dynamicHeader}
            {consentManagementModal}
            {!isJuryRoot && (
                <Header
                    primaryNavigation={navigation.primaryNavigation}
                    primaryNavigationCTA={navigation.primaryNavigationCTA}
                    pageNotFound={pageNotFound}
                    isFocusModeDesktop={isFocusModeDesktop}
                    isFocusModeMobile={isFocusModeMobile}
                />
            )}{' '}
            <ScrollToTop />
            <div className="visually-hidden" id="content" />
            <main className={isJuryRoot ? '' : containerClasses}>
                <Switch>
                    <Route
                        path="/__live_preview"
                        render={(routeProps) => (
                            <LivePreviewPage
                                {...routeProps}
                                onPageDataChange={setPreviewPageData}
                                pageNotFoundCallback={pageNotFoundCallback}
                            />
                        )}
                    />
                    {dynamicPages}
                    <Route path="/projekte/:id" render={(props) => <Project {...props} />} />
                    <Route path="/code-anfordern" render={(props) => <SMSRequest {...props} />} />
                    <Route path="/jury" component={JuryShell} />
                    <Route path="/projekte" render={(props) => <ProjectList />} />
                    <Route path="/bewerbung" render={(props) => <SignUp {...props} />} />
                    <Route path="/logout" component={Logout} />
                    <Route path="/login" component={Auth} />
                    <Route path="/" exact render={(props) => <Home {...props} />} />
                    {pageNotFoundRoute}
                </Switch>
            </main>
            <Footer
                secondaryNavigation={navigation.secondaryNavigation}
                contained={isJuryRoot || !isFooterFullWidth}
                isFocusModeDesktop={isFocusModeDesktop}
                isFocusModeMobile={isFocusModeMobile}
            />
        </div>
    );
}

export default withErrorHandler(App, axios);
